import { useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import type { Powerline } from "types/powerlines.types"

import { BodyContainer } from "widgets/styled/containers"
import DevicesTable from "widgets/device/DevicesTable"
import { useFullDeviceRows } from "helpers/hooks/devices/useFullDeviceRows"
import TowerCardsSection from "widgets/towers/TowerCardsSection"
import { nameOrUnnamed } from "helpers/utils/translations"
import type { TowerWithDevices } from "types/towers.types"

const PowerlineAssets = () => {
  const { t } = useTranslation()
  const { powerline, powerlineFetching } = useOutletContext<{
    powerline: Powerline
    powerlineFetching: boolean
  }>()
  const {
    getPowerlineDevicesRows,
    installedOnLoading,
    statusesLoading,
    isLoading: devicesLoading,
    towersLoading,
  } = useFullDeviceRows()
  const towerIds = useMemo(() => new Set(powerline.tower_ids), [powerline])

  const powerlineDevicesRows = useMemo(
    () => getPowerlineDevicesRows(powerline.id, towerIds),
    [getPowerlineDevicesRows, powerline.id, towerIds],
  )

  const towers: TowerWithDevices[] = useMemo(() => {
    const towerMap = new Map<number, TowerWithDevices>()
    powerlineDevicesRows.all.forEach((row) => {
      const installedOn = row.installedOn
      if (installedOn.type === "tower" && installedOn.tower) {
        const towerId = installedOn.tower.id
        if (!towerMap.has(towerId)) {
          towerMap.set(towerId, { ...installedOn.tower, devices: [] })
        }
        const tower = towerMap.get(towerId)
        if (tower) {
          tower.devices.push({ id: row.device.id, name: row.device.name })
        }
      }
    })
    return Array.from(towerMap.values())
  }, [powerlineDevicesRows])

  return (
    <BodyContainer disableGutters maxWidth="xl">
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ mt: "1rem" }}>
          <TowerCardsSection
            towers={towers}
            isLoading={devicesLoading || towersLoading}
          />
        </Grid>
        <Grid item xs={12}>
          <DevicesTable
            devices={powerlineDevicesRows}
            statusesLoading={statusesLoading}
            installedOnLoading={installedOnLoading}
            isWaiting={devicesLoading || powerlineFetching}
            fileNameEntity={`powerline_${nameOrUnnamed(powerline.name, t)}`}
          />
        </Grid>
      </Grid>
    </BodyContainer>
  )
}

export default PowerlineAssets
